/**
 * @file mobile navigation panelslide
 * @author tobias.frei@notch-interactive.com
 *
 * @module modules/m01-2-mobile-navigation.js
 * @module helpers/helper-dom.js
 *
 * @example
 * <insert example code here>
 */

/* Imports */
import helperDom from '../helpers/helper-dom.js';
import pubsub from '../libs/pubsub';

/**
 * Object with default values.
 * instance values can be overridden by passing an options object when creating a knob instance.
 *
 * @typedef defaults
 */
const defaults = {
    moduleId: 'mobile-navigation',
    navClass: 'mobilenav',
    triggerId: 'trigger',
    type: 'overlaycover',
    levelAttr: 'data-val-level',
    rtl: false,
    hooks: {
        level: '*[data-levelitem]',
        item: '*[data-item]',
        back: '*[data-back]',
        close: '*[data-close]',
        metaNavTrigger: '.mobilenav__meta [data-item]'
    },
    classes: {
        level: 'mobilenav__level',
        levelOverlay: 'mobilenav__level-overlay',
        disableScroll: 'body--noscroll',
        fixheight: 'body--fixheight'
    },
    states: {
        ready: 'is-ready',
        levelOpen: 'is-open',
        levelActive: 'is-active',
        rtl: 'is-rtl'
    }
};

/**
 * Initialize module
 *
 * @param {object=} options - Override default settings with options object.
 * @param {string=} options.moduleId - The element id of the mobile navigation wrapper.
 * @param {string=} options.navClass - The element class of the mobile navigation.
 * @param {string=} options.triggerId - The element id of the trigger button.
 * @param {string=} options.type - The modification type of the mobile navigation.
 * @param {string=} options.levelAttr - The attribute for the level numbers.
 * @param {boolean=} options.rtl - The flag for right to left animation.
 * @param {object=} options.hooks - The settings for js hook selectors.
 * @param {string=} options.hooks.item - The selector name of the trigger items.
 * @param {string=} options.hooks.back - The selector name of the back buttons.
 * @param {string=} options.hooks.close - The selector name of the close buttons.
 * @param {object=} options.classes - The settings for dynamic classes.
 * @param {string=} options.classes.level - The class name of the level elements.
 * @param {string=} options.classes.levelOverlay - The class name of the level overlay elements.
 * @param {object=} options.states - The states classes.
 * @param {string=} options.states.ready - The ready state for overlay animation.
 * @param {string=} options.states.levelOpen - The open state for the level elements.
 * @param {string=} options.states.rtl - The state for right to left mode.
 * @return {object} Instance of created module.
 */
export default (options) => {

    /* private letiables */
    const instance = {};
    let settings,
        trigger,
        metaNavTriggers,
        open,
        level,
        levels,
        menuItems,
        levelBack,
        closeBtns,
        mobileNav,
        mainModule,
        scrollTop,
        closeEvent,
        sublevel, // edge bug

        /* private methods */
        bindEvents = () => {

            window.addEventListener('resize', function () {
                // set new wrapper width on active mobilenav
                if (mainModule.classList.contains('mobilenav--pushed')) {
                    let translateVal = mobileNav.offsetWidth;
                    if (settings.rtl) {
                        translateVal = translateVal * -1;
                    }
                    setTransform('translate3d(' + translateVal + 'px, 0, 0)');
                }
            }, true);

            // open and close the menu
            trigger.addEventListener('click', function (ev) {
                ev.stopPropagation();
                ev.preventDefault();

                if (open) {
                    resetMenu();
                } else {
                    // get window position top for restore on menu close
                    scrollTop = window.pageYOffset;

                    // set ready state on wrapper for overlay
                    mainModule.classList.add(settings.states.ready);

                    // open the menu
                    openMenu();
                }

            });

            metaNavTriggers.forEach((trigger) => {
                trigger.addEventListener('click', () => {
                    pubsub.trigger('mobilenav.open');
                });
            });

            // open sub level
            menuItems.forEach(function (menuItem) {
                let subLevel;

                // get sublevel in children if exist
                for (let i = 0; i < menuItem.childNodes.length; i++) {
                    if (menuItem.childNodes[i].className === settings.classes.level) {
                        subLevel = menuItem.childNodes[i];
                        break;
                    }
                }

                if (subLevel) {
                    menuItem.querySelector('a').addEventListener('click', function (ev) {
                        if (ev.target.classList.contains('indicatorbox')) {
                            return false;
                        }
                        ev.preventDefault();
                        let level = menuItem.closest('.' + settings.classes.level).getAttribute(settings.levelAttr);
                        if (level <= level) {
                            ev.stopPropagation();
                            menuItem.closest('.' + settings.classes.level).classList.add(settings.classes.levelOverlay);
                            openMenu(subLevel);
                        }
                    });
                }
            });

            // event on level back buttons
            levelBack.forEach(function (levelBackBtn) {
                levelBackBtn.addEventListener('click', function (ev) {
                    ev.preventDefault();
                    let elLevel = levelBackBtn.closest('.' + settings.classes.level).getAttribute(settings.levelAttr);
                    if (level <= elLevel) {
                        ev.stopPropagation();
                        level = levelBackBtn.closest('.' + settings.classes.level).getAttribute(settings.levelAttr) - 1;
                        if (level === 0) {
                            resetMenu();
                        } else {
                            closeMenu();
                        }
                    }
                });
            });

            // event on level back buttons
            closeBtns.forEach(function (closeBtn) {
                closeBtn.addEventListener('click', function (ev) {
                    ev.preventDefault();
                    mainModule.dispatchEvent(closeEvent);
                    resetMenu();
                });
            });
        },
        start = () => {
            // flag if menu open
            open = false;

            // init start level depth
            level = 0;

            // set rtl
            if (settings.rtl) {
                mainModule.classList.add(settings.states.rtl);
            }

            // get level elements
            levels = [...mobileNav.querySelectorAll('div.' + settings.classes.level)];

            // set depth of level elements
            levels.forEach((levelElement) => {
                levelElement.setAttribute(settings.levelAttr, helperDom.getLevelDepth(levelElement, mobileNav.id, settings.classes.level));
            });

            // get menu items
            menuItems = [...mobileNav.querySelectorAll(settings.hooks.item)];

            // serve hooks to move back on previous level
            levelBack = [...mobileNav.querySelectorAll(settings.hooks.back)];

            // serve hooks to move back on previous level
            closeBtns = [...mobileNav.querySelectorAll(settings.hooks.close)];

            // set the modification class
            mobileNav.classList.add('mobilenav__' + settings.type);

            // bind events
            bindEvents();
        },
        openMenu = (subLevel) => {
            // increment level depth
            ++level;

            // move the main wrapper
            let levelFactor = (level - 1),
                translateVal = mobileNav.offsetWidth,
                mpLevelEl;

            if (settings.rtl) {
                translateVal = translateVal * -1;
            }

            // calc scrollbar
            translateVal = translateVal - (window.innerWidth - document.documentElement.clientWidth);

            // clear active states
            clearActiveStates();

            setTransform('translate3d(' + translateVal + 'px, 0, 0)');

            if (subLevel) {
                let i,
                    len = levels.length;
                // reset sublevel transformation
                setTransform('', subLevel);
                // need to reset the translate value for the level menus that have the same level depth and arent open
                for (i = 0; i < len; ++i) {
                    let levelEl = levels[i];
                    if (levelEl !== subLevel && !levelEl.classList.contains(settings.states.levelOpen)) {
                        if (settings.rtl) {
                            setTransform('translate3d(100%,0,0) translate3d(' + -1 * levelFactor + 'px, 0, 0)', levelEl);
                        } else {
                            setTransform('translate3d(-100%,0,0) translate3d(' + -1 * levelFactor + 'px, 0, 0)', levelEl);
                        }
                    }
                }
            }

            // add class mobilenav--pushed to main wrapper if opening the first time
            if (level === 1) {
                mainModule.classList.add('mobilenav--pushed');
                open = true;
            }

            // add level open state to the opening level element
            mpLevelEl = subLevel || levels[0];

            // add class states
            mpLevelEl.classList.add(settings.states.levelActive);
            mpLevelEl.classList.add(settings.states.levelOpen);

            // edge fix
            if (typeof sublevel !== undefined) {
                if (!sublevel) {
                    // add overflow-y: hidden to body on first opening
                    document.body.classList.add(settings.classes.disableScroll);
                    setTimeout(function () {
                        document.body.classList.add(settings.classes.fixheight);
                    }, 300);
                }
            }

            // trigger pubsub event
            pubsub.trigger('mobilenav.open');
        },
        resetMenu = () => {
            clearActiveStates();
            setTransform('translate3d(0, 0, 0)');
            level = 0;

            // enable scroll and jump to init scrolltop
            document.body.classList.remove(settings.classes.disableScroll);
            document.body.classList.remove(settings.classes.fixheight);
            window.scrollTo(0, scrollTop);

            // remove class mobilenav--pushed from main wrapper
            mainModule.addEventListener('transitionend', onNavClosed, false);
            mainModule.classList.remove('mobilenav--pushed');
            toggleLevels();
            open = false;
        },
        onNavClosed = (e) => {
            // remove is-ready state
            if (e.propertyName === 'transform' && e.type.toLowerCase().indexOf('transitionend') >= 0) {
                mainModule.classList.remove(settings.states.ready);
                mainModule.removeEventListener('transitionend', onNavClosed, false);
            }
        },
        closeMenu = () => {
            let translateVal = mobileNav.offsetWidth;

            // change direction on rtl
            if (settings.rtl) {
                translateVal = translateVal * -1;
            }

            // clear active states before anim close
            clearActiveStates();

            // anim close
            setTransform('translate3d(' + translateVal + 'px,0,0)');

            // add active state to actual level
            mainModule.querySelector('.' + settings.classes.level + '.' + settings.states.levelOpen + '[' + settings.levelAttr + '="' + level + '"]').classList.add(settings.states.levelActive);

            toggleLevels();
        },
        setTransform = (val, el) => {
            el = el || mainModule;
            el.style.transform = val;
        },
        clearActiveStates = () => {
            // clear active states
            [...mainModule.querySelectorAll('.' + settings.states.levelActive)].forEach(activeLevel => {
                activeLevel.classList.remove(settings.states.levelActive);
            });
        },
        toggleLevels = () => {
            for (let i = 0, len = levels.length; i < len; ++i) {
                let levelEl = levels[i];
                if (levelEl.getAttribute(settings.levelAttr) >= level + 1) {
                    levelEl.classList.remove(settings.states.levelOpen);
                    levelEl.classList.remove(settings.states.levelActive);
                    levelEl.classList.remove(settings.classes.levelOverlay);
                } else if (Number(levelEl.getAttribute(settings.levelAttr)) === level) {
                    levelEl.classList.remove(settings.classes.levelOverlay);
                }
            }
        };


    settings = Object.assign({}, defaults, options);

    // get main elements
    mainModule = document.getElementById(settings.moduleId);

    if (!mainModule) {
        return false;
    }

    mobileNav = mainModule.querySelector('.' + settings.navClass);
    trigger = document.getElementById(settings.triggerId);
    metaNavTriggers = document.querySelectorAll(settings.hooks.metaNavTrigger);

    if (!trigger) {
        console.error('no calendar nav trigger found');
        return false;
    }

    // support 3d transforms
    if (mainModule && mobileNav && Modernizr.csstransforms3d) {
        closeEvent = document.createEvent('Event');
        closeEvent.initEvent('closenav', true, true);

        start();
    }

    return instance;
};
